import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, List, ListOrdered, Strikethrough } from "lucide-react";
import { sanitizeVenuePolicyHtml } from "../lib/venuePolicyHtml";

const POLICY_STARTER_KIT = StarterKit.configure({
  heading: false,
  codeBlock: false,
  blockquote: false,
  code: false,
  horizontalRule: false,
});

const ToolbarButton = ({ active, disabled, label, onClick, children }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={active}
    disabled={disabled}
    onClick={onClick}
    className={`inline-flex items-center justify-center gap-1.5 min-h-9 px-3 py-1.5 text-sm font-semibold rounded transition disabled:opacity-50 disabled:cursor-not-allowed ${
      active
        ? "bg-brand-maroon text-white"
        : "bg-white border border-gray-300 text-gray-900 hover:bg-gray-100"
    }`}
  >
    {children}
  </button>
);

const VenuePolicyEditor = ({
  value = "",
  onChange,
  contentKey = "new",
  disabled = false,
}) => {
  const editor = useEditor({
    extensions: [POLICY_STARTER_KIT],
    immediatelyRender: false,
    content: sanitizeVenuePolicyHtml(value) || "",
    editorProps: {
      attributes: {
        class:
          "min-h-[200px] max-h-[360px] overflow-auto px-4 py-3 text-sm text-gray-900 focus:outline-none [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_strong]:font-bold [&_em]:italic [&_s]:line-through",
      },
    },
    onUpdate: ({ editor: nextEditor }) => {
      onChange?.(nextEditor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(sanitizeVenuePolicyHtml(value) || "", false);
    // Reset only when a different venue record is loaded, never on each keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, contentKey]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  return (
    <div className={`border border-gray-200 rounded-xl overflow-hidden min-w-0 ${disabled ? "bg-gray-50" : "bg-white"}`}>
      <div className="flex flex-wrap gap-1 p-3 border-b border-gray-200 bg-gray-50">
        <ToolbarButton
          label="Bold"
          active={editor.isActive("bold")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold size={14} />
          B
        </ToolbarButton>
        <ToolbarButton
          label="Italic"
          active={editor.isActive("italic")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic size={14} />
          I
        </ToolbarButton>
        <ToolbarButton
          label="Strikethrough"
          active={editor.isActive("strike")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough size={14} />
          S
        </ToolbarButton>
        <ToolbarButton
          label="Bullet list"
          active={editor.isActive("bulletList")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List size={14} />
          List
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          active={editor.isActive("orderedList")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered size={14} />
          1. List
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default VenuePolicyEditor;
